import React, {
  ComponentType, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { format } from 'date-fns';

import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import Icon from 'components/DesignSystem/Icon';
import SelectorChip from 'components/DesignSystem/SelectorChip';
import DoubleSlider from 'components/DesignSystem/Slider/DoubleSlider';
import CentralizedSpinner from 'components/CentralizedSpinner';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';
import { MEDIUM_BLUE } from 'constants/colors';
import useDownloadDivImage from 'hooks/useDownloadDivImage';

import type { IconType } from 'components/DesignSystem/Icon/icons';
import type { ResponseError } from 'services/api/base';
import { cssVar } from 'constants/theme';

import { useSeriesColor } from 'hooks/theme/useChartTheme';
import './index.scss';

export type DVOptionKey = 'value' | 'name';

/** Opção de variável; o identificador textual vem em `value` (Sequencial) ou `name` (Cultura). */
export interface DVChartOption {
  id: number;
  label: string;
  value?: string;
  name?: string;
}

export interface DVGraphData {
  label?: string;
  name: string;
  data: { date: string; value: number }[];
}

export interface DVLegendItem {
  label: string;
  color: string;
  shape: 'bar' | 'line';
}

export interface DVChartProps<G extends DVGraphData> {
  zoomStart: number;
  zoomEnd: number;
  height?: string;
  hideTitle?: boolean;
  hideDataZoom?: boolean;
  graphData?: G;
  combinedData?: { chuva: G; temperatura: G };
}

export interface DVFullscreenProps<G extends DVGraphData> {
  open: boolean;
  dismiss: () => void;
  title?: string;
  legend?: DVLegendItem[];
  zoomStart: number;
  zoomEnd: number;
  graphData?: G;
  combinedData?: { chuva: G; temperatura: G };
}

export interface DVEmptyStateProps {
  icon: IconType;
  title: string;
  text: string;
  actionLabel?: string;
  onAction?: () => void;
}

/** Peças que mudam por domínio (cada uma tem estilo/dados próprios). */
export interface DVCardMobileComponents<G extends DVGraphData> {
  Chart: ComponentType<DVChartProps<G>>;
  Fullscreen: ComponentType<DVFullscreenProps<G>>;
  About: ComponentType<{ show: boolean; onHide: () => void }>;
  ControlPanelSheet: ComponentType<{ open: boolean; dismiss: () => void }>;
  EmptyState: ComponentType<DVEmptyStateProps>;
}

export interface DVCardMobileLayoutProps<G extends DVGraphData> {
  /** Campo da opção que identifica a variável: `value` (Sequencial) ou `name` (Cultura). */
  optionKey: DVOptionKey;
  /** Título do QueryCard (nome do local selecionado). */
  title?: string;
  /** Etiquetas do QueryCard, já na ordem de exibição. */
  chips: string[];
  /** Prefixo do PNG baixado; vira `${filenamePrefix}_${variaveis}.png`. */
  filenamePrefix: string;
  /** Texto do estado "Nenhuma consulta ainda". */
  emptyText: string;
  graphData?: G[];
  graphDataLoading: boolean;
  /** `params !== undefined` no hook do domínio: há consulta feita. */
  hasParams: boolean;
  queryError?: ResponseError;
  onRetry: () => void;
  chartVariable: {
    chartOptions: DVChartOption[];
    selectedChartVariables: number[];
    allSelected: boolean;
    toggleChartVariables: (id: number) => void;
    selectAll: () => void;
    deselectAll: () => void;
    isLoading: boolean;
  };
  period: { startDate?: Date | null; endDate?: Date | null };
  components: DVCardMobileComponents<G>;
}

const MOBILE_CHART_HEIGHT = '16rem';

const COMBINED_LEGEND = [
  { label: 'Chuva (mm)', color: MEDIUM_BLUE, shape: 'bar' as const }, // tema: cor de dado
  { label: 'Temperatura (°C)', color: '#006400', shape: 'line' as const }, // tema: cor de dado
];
const DEFEXC_LEGEND = [
  { label: 'DEF', color: '#d32f2f', shape: 'bar' as const }, // tema: cor de dado
  { label: 'EXC', color: MEDIUM_BLUE, shape: 'bar' as const }, // tema: cor de dado
];

export default function DVCardMobileLayout<G extends DVGraphData>({
  optionKey,
  title,
  chips,
  filenamePrefix,
  emptyText,
  graphData,
  graphDataLoading,
  hasParams,
  queryError,
  onRetry,
  chartVariable,
  period,
  components,
}: DVCardMobileLayoutProps<G>) {
  const {
    Chart, Fullscreen, About, ControlPanelSheet, EmptyState,
  } = components;
  const corSerie = useSeriesColor();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [fullscreenCardKey, setFullscreenCardKey] = useState<string | null>(null);
  const dvImages = useRef<HTMLDivElement>(null);
  const downloadChartsAsImage = useDownloadDivImage(dvImages);

  const {
    chartOptions,
    selectedChartVariables,
    allSelected,
    toggleChartVariables,
    selectAll,
    deselectAll,
    isLoading: variablesLoading,
  } = chartVariable;

  const selectedVariableNames = selectedChartVariables
    .map((variableId) => chartOptions.find((o) => o.id === variableId)?.[optionKey])
    .filter((v) => v) as string[];

  const charts = useMemo(
    () => graphData?.map((data) => ({
      ...data,
      label: chartOptions.find((o) => o[optionKey] === data.name)?.label || '',
    })),
    [graphData, chartOptions, optionKey],
  );

  const hasChuvaAndTemperatura = useMemo(() => (
    selectedVariableNames.includes('chuva') && selectedVariableNames.includes('temperatura')
  ), [selectedVariableNames]);

  const filteredVariableNames = useMemo(
    () => selectedVariableNames.filter(
      (name) => name !== 'chuva' && name !== 'temperatura',
    ),
    [selectedVariableNames],
  );

  const displayChartOptions = useMemo(
    () => chartOptions.filter(
      (option) => option[optionKey] !== 'chuva' && option[optionKey] !== 'temperatura',
    ),
    [chartOptions, optionKey],
  );
  const chuvaOption = useMemo(
    () => chartOptions.find((o) => o[optionKey] === 'chuva'),
    [chartOptions, optionKey],
  );
  const temperaturaOption = useMemo(
    () => chartOptions.find((o) => o[optionKey] === 'temperatura'),
    [chartOptions, optionKey],
  );
  const hasCombinedOption = !!chuvaOption && !!temperaturaOption;
  const isCombinedSelected = useMemo(() => {
    if (!hasCombinedOption || !chuvaOption || !temperaturaOption) return false;
    return selectedChartVariables.includes(chuvaOption.id)
      && selectedChartVariables.includes(temperaturaOption.id);
  }, [selectedChartVariables, hasCombinedOption, chuvaOption, temperaturaOption]);

  const toggleCombinedVariables = useCallback(() => {
    if (!hasCombinedOption || !chuvaOption || !temperaturaOption) return;

    if (isCombinedSelected) {
      toggleChartVariables(chuvaOption.id);
      toggleChartVariables(temperaturaOption.id);
    } else {
      if (!selectedChartVariables.includes(chuvaOption.id)) {
        toggleChartVariables(chuvaOption.id);
      }
      if (!selectedChartVariables.includes(temperaturaOption.id)) {
        toggleChartVariables(temperaturaOption.id);
      }
    }
  }, [
    hasCombinedOption,
    isCombinedSelected,
    toggleChartVariables,
    chuvaOption,
    temperaturaOption,
    selectedChartVariables,
  ]);

  const [zoomStart, setZoomStart] = useState(0);
  const [zoomEnd, setZoomEnd] = useState(100);
  const setZoom = useCallback(([start, end]: number[]) => {
    setZoomStart(start);
    setZoomEnd(end);
  }, []);

  const zoomLabel = useMemo(() => {
    if (!period.startDate || !period.endDate) return undefined;
    const totalMs = period.endDate.getTime() - period.startDate.getTime();
    const from = new Date(period.startDate.getTime() + (totalMs * zoomStart) / 100);
    const to = new Date(period.startDate.getTime() + (totalMs * zoomEnd) / 100);
    return `${format(from, 'dd/MM/yy')} – ${format(to, 'dd/MM/yy')}`;
  }, [period.startDate, period.endDate, zoomStart, zoomEnd]);

  const chuvaChart = charts?.find((d) => d.name === 'chuva');
  const temperaturaChart = charts?.find((d) => d.name === 'temperatura');

  const hasQuery = charts !== undefined && hasParams;
  const hasVariableSelected = filteredVariableNames.length > 0 || hasChuvaAndTemperatura;

  const chartCards = useMemo(() => {
    const cards: {
      key: string;
      title: string;
      legend?: DVLegendItem[];
      graphData?: G;
      combinedData?: { chuva: G; temperatura: G };
    }[] = [];

    if (hasChuvaAndTemperatura && chuvaChart && temperaturaChart) {
      cards.push({
        key: 'chuva-temperatura',
        title: 'Chuva e Temperatura',
        legend: COMBINED_LEGEND,
        combinedData: { chuva: chuvaChart, temperatura: temperaturaChart },
      });
    }

    filteredVariableNames.forEach((variableName) => {
      cards.push({
        key: variableName,
        title: chartOptions.find((o) => o[optionKey] === variableName)?.label || '',
        legend: variableName === 'def_x_exc' ? DEFEXC_LEGEND : undefined,
        graphData: charts?.find((data) => data.name === variableName),
      });
    });

    return cards;
  }, [
    hasChuvaAndTemperatura, chuvaChart, temperaturaChart, filteredVariableNames,
    chartOptions, charts, optionKey,
  ]);

  const fullscreenCard = fullscreenCardKey
    ? chartCards.find((card) => card.key === fullscreenCardKey)
    : undefined;

  useEffect(() => {
    if (charts && selectedVariableNames.length === 0) selectAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charts, selectAll]);

  const filename = `${filenamePrefix}_${selectedVariableNames.join('_')}.png`;

  const variableChips = [
    ...(hasCombinedOption ? [{
      key: 'chuva-temperatura',
      label: 'Chuva e Temperatura',
      checked: isCombinedSelected,
      onToggle: toggleCombinedVariables,
    }] : []),
    ...displayChartOptions.map((option) => ({
      key: option[optionKey] ?? String(option.id),
      label: option.label,
      checked: selectedChartVariables.includes(option.id),
      onToggle: () => toggleChartVariables(option.id),
    })),
    {
      key: 'all',
      label: 'Todos',
      checked: allSelected,
      onToggle: () => (allSelected ? deselectAll() : selectAll()),
    },
  ];

  return (
    <div className="">
      <QueryCard>
        <QueryCardHeader label="Consulta atual" title={title}>
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger onClick={() => setSheetOpen(true)} />
      </QueryCard>

      {(variablesLoading || graphDataLoading) && <CentralizedSpinner />}

      {!variablesLoading && !graphDataLoading && queryError && (
        <QueryErrorState
          title={queryError.data}
          message={queryError.description}
          onRetry={onRetry}
        />
      )}

      {!variablesLoading && !graphDataLoading && !queryError && !hasQuery && (
        <EmptyState
          icon="filter"
          title="Nenhuma consulta ainda"
          text={emptyText}
          actionLabel="Abrir painel"
          onAction={() => setSheetOpen(true)}
        />
      )}

      {!variablesLoading && !graphDataLoading && !queryError && hasQuery && (
        <div className="agro-wb-dv-mobile-panel">
          <div className="agro-wb-dv-mobile-controls">
            <div className="agro-wb-dv-mobile-chips">
              {variableChips.map((chip) => {
                const isAllChip = chip.key === 'all';
                return (
                  <SelectorChip
                    key={chip.key}
                    role="checkbox"
                    tone={isAllChip ? 'accent' : 'soft'}
                    selected={chip.checked}
                    onClick={chip.onToggle}
                  >
                    {chip.checked && (
                      <Icon
                        iconType="checkmark"
                        color={cssVar(isAllChip ? 'text-on-accent' : 'text-accent')}
                        size="small"
                      />
                    )}
                    <span>{chip.label}</span>
                  </SelectorChip>
                );
              })}
            </div>
            <button
              type="button"
              aria-label="Baixar imagem"
              className="agro-wb-dv-mobile-action"
              onClick={downloadChartsAsImage(filename)}
              disabled={selectedChartVariables.length === 0}
            >
              <Icon iconType="download" color={cssVar('text-strong')} size="small" />
            </button>
            <button
              type="button"
              aria-label="Instruções"
              className="agro-wb-dv-mobile-action agro-wb-dv-mobile-action-info"
              onClick={() => setShowAbout(true)}
            >
              <Icon iconType="info" color={cssVar('text-on-accent')} size="small" />
            </button>
          </div>

          {!hasVariableSelected && (
            <EmptyState
              icon="line_axis"
              title="Selecione uma variável"
              text="Marque ao menos uma variável acima para ver os gráficos."
            />
          )}

          {hasVariableSelected && (
            <>
              <div ref={dvImages} className="agro-wb-dv-mobile-chart-stack">
                {chartCards.map((card) => (
                  <div key={card.key} className="agro-wb-dv-mobile-chart-card">
                    <div className="agro-wb-dv-mobile-chart-card-header">
                      <span className="agro-wb-dv-mobile-chart-card-title">{card.title}</span>
                      <button
                        type="button"
                        aria-label="Tela cheia"
                        className="agro-wb-dv-mobile-chart-card-fullscreen"
                        onClick={() => setFullscreenCardKey(card.key)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
                        </svg>
                      </button>
                    </div>
                    {card.legend && (
                      <div className="agro-wb-dv-mobile-chart-card-legend">
                        {card.legend.map((item) => (
                          <span key={item.label} className="agro-wb-dv-mobile-chart-card-legend-item">
                            <span
                              className={`agro-wb-dv-mobile-chart-card-legend-swatch ${item.shape}`}
                              style={{ backgroundColor: corSerie(item.color) }}
                            />
                            {item.label}
                          </span>
                        ))}
                      </div>
                    )}
                    <Chart
                      zoomStart={zoomStart}
                      zoomEnd={zoomEnd}
                      height={MOBILE_CHART_HEIGHT}
                      hideTitle
                      hideDataZoom
                      graphData={card.graphData}
                      combinedData={card.combinedData}
                    />
                  </div>
                ))}
              </div>
              <div className="agro-wb-dv-mobile-zoom-bar">
                <div className="agro-wb-dv-mobile-zoom-bar-row">
                  <span className="agro-wb-dv-mobile-zoom-bar-label">
                    <Icon iconType="search" color={cssVar('text-strong')} size="small" />
                    Zoom sincronizado
                  </span>
                  {zoomLabel && (
                    <span className="agro-wb-dv-mobile-zoom-bar-range">{zoomLabel}</span>
                  )}
                </div>
                <DoubleSlider
                  min={0}
                  max={100}
                  value={[zoomStart, zoomEnd]}
                  setValue={setZoom}
                />
                {period.startDate && period.endDate && (
                  <div className="agro-wb-dv-mobile-zoom-bar-bounds">
                    <span>{format(period.startDate, 'dd/MM')}</span>
                    <span>{format(period.endDate, 'dd/MM')}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <About show={showAbout} onHide={() => setShowAbout(false)} />
      <ControlPanelSheet open={sheetOpen} dismiss={() => setSheetOpen(false)} />
      <Fullscreen
        open={!!fullscreenCard}
        dismiss={() => setFullscreenCardKey(null)}
        title={fullscreenCard?.title}
        legend={fullscreenCard?.legend}
        zoomStart={zoomStart}
        zoomEnd={zoomEnd}
        graphData={fullscreenCard?.graphData}
        combinedData={fullscreenCard?.combinedData}
      />
    </div>
  );
}
